import { readFileSync, existsSync } from 'fs';
import type { FrameworkIntegration, DetectionResult, IntegrationConfig } from './types.js';

interface PackageJson {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}

export const vercelAiIntegration: FrameworkIntegration = {
  name: 'vercel-ai',
  description: 'Vercel AI SDK',

  detect(): Promise<DetectionResult> {
    let hasAiSdk = false;

    if (existsSync('package.json')) {
      try {
        const pkg = JSON.parse(readFileSync('package.json', 'utf-8')) as PackageJson;
        const deps: Record<string, string> = { ...pkg.dependencies, ...pkg.devDependencies };
        hasAiSdk = 'ai' in deps;
      } catch {
        // Ignore
      }
    }

    return Promise.resolve({
      detected: hasAiSdk,
      confidence: hasAiSdk ? 0.9 : 0,
      files: hasAiSdk ? ['package.json'] : [],
    });
  },

  generate(config: IntegrationConfig): Promise<string> {
    return Promise.resolve(`---
name: mutagent-vercel-ai-integration
description: Integrate MutagenT with Vercel AI SDK for streaming and edge functions
framework: vercel-ai
version: 2.0.0
---

# MutagenT + Vercel AI SDK Integration

## Prerequisites Verification

- MUTAGENT_API_KEY: ${config.apiKey.slice(0, 8)}...${config.apiKey.slice(-4)}
- MUTAGENT_ENDPOINT: ${config.endpoint}
- API Connection: Verified

## Installation

\`\`\`bash
bun add @mutagent/vercel-ai @mutagent/sdk @opentelemetry/sdk-trace-node @opentelemetry/sdk-trace-base
# or
npm install @mutagent/vercel-ai @mutagent/sdk @opentelemetry/sdk-trace-node @opentelemetry/sdk-trace-base
\`\`\`

## Option A: OpenTelemetry (Recommended)

Follows the same pattern as Langfuse, Braintrust, SigNoz, and other major observability providers.
Uses \`MutagentSpanExporter\` as a standard OTel SpanProcessor — no model wrapping required.

\`\`\`typescript
import { generateText } from 'ai';
import { openai } from '@ai-sdk/openai';
import { MutagentSpanExporter } from '@mutagent/vercel-ai';
import { NodeTracerProvider } from '@opentelemetry/sdk-trace-node';
import { SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';

// NOTE: OpenTelemetry v2.x required. The v1 API (addSpanProcessor) was removed.
// Users on OTel v1 should upgrade or use createMutagentMiddleware() alternative.

// OpenTelemetry v2 API: Use constructor-based span processor registration
const provider = new NodeTracerProvider({
  spanProcessors: [
    new SimpleSpanProcessor(new MutagentSpanExporter())
  ]
});
provider.register();

// Enable telemetry on AI SDK calls — all calls are automatically traced
const result = await generateText({
  model: openai('gpt-4o'),
  prompt: 'Hello!',
  experimental_telemetry: { isEnabled: true },
});
\`\`\`

### Streaming with OTel

\`\`\`typescript
// app/api/chat/route.ts
import { streamText } from 'ai';
import { openai } from '@ai-sdk/openai';

export async function POST(req: Request) {
  const { messages } = await req.json();

  // OTel provider registered at startup — just enable telemetry
  const result = streamText({
    model: openai('gpt-4o'),
    messages,
    experimental_telemetry: { isEnabled: true },
  });

  return result.toDataStreamResponse();
}
\`\`\`

## Option B: Middleware (Alternative — Simple / Legacy)

Works without OTel setup. Good for quick prototyping or environments where
installing OTel dependencies is not practical.

\`\`\`typescript
import { generateText, wrapLanguageModel } from 'ai';
import { openai } from '@ai-sdk/openai';
import { createMutagentMiddleware } from '@mutagent/vercel-ai';

const middleware = createMutagentMiddleware();
const model = wrapLanguageModel({ model: openai('gpt-4o'), middleware });

const { text } = await generateText({
  model,
  prompt: 'What is TypeScript?',
});
\`\`\`

### Streaming with Middleware

\`\`\`typescript
// app/api/chat/route.ts
import { streamText, wrapLanguageModel } from 'ai';
import { openai } from '@ai-sdk/openai';
import { createMutagentMiddleware } from '@mutagent/vercel-ai';

const middleware = createMutagentMiddleware();
const model = wrapLanguageModel({ model: openai('gpt-4o'), middleware });

export async function POST(req: Request) {
  const { messages } = await req.json();

  const result = streamText({ model, messages });

  return result.toDataStreamResponse();
}
\`\`\`

## Verification

\`\`\`bash
mutagent integrate vercel-ai --verify
\`\`\`

## Documentation

- Full guide: https://docs.mutagent.io/integrations/vercel-ai
- API Reference: https://docs.mutagent.io/sdk/tracing
- Dashboard: https://app.mutagent.io
`);
  },
};
